import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Header,
  Footer,
  WhatsAppButton,
  DoctorBanner,
  PatientReviews,
  DesignIcon,
  asset,
  PEBBLE_URL,
} from "../components/GuestSite";
import ArticleSeo from "./articles/ArticleSeo";
import "../components/pebble-refresh.css";
const steps = [
  { title: "Message Pebble on WhatsApp", description: "Say hi to Pebble. No sign-up, no app download." },
  { title: "Buy airtime or data", description: "Top up as you normally do. Ask Pebble about eligible purchases and the contribution toward hospital cash cover." },
  { title: "Get admitted", description: "Ask Pebble for the cover terms, eligibility and waiting period that apply to hospital admissions and A&E visits." },
  {
    title: "Send proof, get paid",
    description: "Send your hospital proof of admission on WhatsApp. If eligible, you receive the applicable hospital cash benefit for each covered 24-hour period. Ask Pebble about claim verification and payment timing.",
  },
];
// Repeat the four steps so the track can move forward continuously;
// the duplicate window is then reset invisibly after the transition.
const loopingSteps = Array.from({ length: 4 }, () => steps).flat();


const benefitCards = [
  { id: "basic-rose", type: "life", accent: "rose", title: "Pebble Basic",
    description: "Protection built around everyday top-ups.",
    features: ["Hospital cash support", "Simple WhatsApp-based guidance"] },
  { id: "plus-mint", type: "cash", accent: "mint", title: "Pebble Plus",
    description: "Explore added support when it matters most.",
    features: ["Hospital admission support", "Clear benefit and eligibility information"] },
  { id: "basic-lavender", type: "life", accent: "lavender", title: "Pebble Basic",
    description: "Protection built around everyday top-ups.",
    features: ["Hospital cash support", "Simple WhatsApp-based guidance"] },
  { id: "plus-peach", type: "cash", accent: "peach", title: "Pebble Plus",
    description: "Explore added support when it matters most.",
    features: ["Hospital admission support", "Clear benefit and eligibility information"] },
];
const benefitLoop = [...benefitCards, ...benefitCards, ...benefitCards];

function BenefitsCarousel() {
  const [cursor, setCursor] = useState(4);
  const [perPage, setPerPage] = useState(2);
  const [stepWidth, setStepWidth] = useState(0);
  const [resetting, setResetting] = useState(false);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const trackRef = useRef(null);
  const resetTimer = useRef(null);
  const moving = useRef(false);
  const touchStart = useRef(null);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const measure = () => {
      const first = track.children[0];
      const second = track.children[1];
      if (first && second) setStepWidth(second.offsetLeft - first.offsetLeft);
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(track);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const viewportQuery = window.matchMedia("(max-width: 640px)");
    const updateMotion = () => setReducedMotion(motionQuery.matches);
    const updateViewport = () => setPerPage(viewportQuery.matches ? 1 : 2);
    updateMotion();
    updateViewport();
    motionQuery.addEventListener?.("change", updateMotion);
    viewportQuery.addEventListener?.("change", updateViewport);
    return () => {
      motionQuery.removeEventListener?.("change", updateMotion);
      viewportQuery.removeEventListener?.("change", updateViewport);
    };
  }, []);

  useEffect(() => () => {
    if (resetTimer.current != null) window.clearTimeout(resetTimer.current);
  }, []);

  const move = (amount) => {
    if (moving.current || resetting || !stepWidth) return;
    if (!reducedMotion) moving.current = true;
    setCursor((current) => {
      const increment = amount * perPage;
      if (reducedMotion) return 4 + (((current - 4 + increment) % 4 + 4) % 4);
      return current + increment;
    });
  };

  useEffect(() => {
    if (paused || resetting || reducedMotion || !stepWidth) return undefined;
    const id = window.setInterval(() => {
      if (moving.current) return;
      moving.current = true;
      setCursor((current) => current + perPage);
    }, 5000);
    return () => window.clearInterval(id);
  }, [paused, resetting, reducedMotion, stepWidth, perPage]);

  const onSlideEnd = (event) => {
    if (event.target !== event.currentTarget || event.propertyName !== "transform") return;
    moving.current = false;
    if (cursor < 4 || cursor >= 8) {
      setResetting(true);
      setCursor((current) => current < 4 ? current + 4 : current - 4);
      if (resetTimer.current != null) window.clearTimeout(resetTimer.current);
      resetTimer.current = window.setTimeout(() => {
        setResetting(false);
        resetTimer.current = null;
      }, 80);
    }
  };

  const pageCount = benefitCards.length / perPage;
  const activePage = Math.floor((((cursor % 4) + 4) % 4) / perPage);
  const goTo = (target) => {
    let amount = (target - activePage + pageCount) % pageCount;
    if (amount > pageCount / 2) amount -= pageCount;
    if (amount) move(amount);
  };

  return (
    <div
      className="zv-benefits-carousel"
      aria-label="Pebble insurance benefits"
      aria-roledescription="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
      onTouchStart={(event) => { touchStart.current = event.touches[0]?.clientX ?? null; }}
      onTouchEnd={(event) => {
        if (touchStart.current == null) return;
        const endX = event.changedTouches[0]?.clientX ?? touchStart.current;
        const distance = touchStart.current - endX;
        touchStart.current = null;
        if (Math.abs(distance) > 45) move(distance > 0 ? 1 : -1);
      }}
    >
      <div className="zv-benefits-viewport">
        <div
          ref={trackRef}
          className={"zv-benefits-grid zv-benefits-track" + (resetting || reducedMotion ? " is-resetting" : "")}
          style={{ transform: "translate3d(-" + (cursor * stepWidth) + "px, 0, 0)", opacity: stepWidth ? 1 : 0 }}
          onTransitionEnd={onSlideEnd}
        >
          {benefitLoop.map((card, renderIndex) => {
            const position = renderIndex - cursor;
            const interactive = position >= 0 && position < perPage;
            return (
              <article
                key={Math.floor(renderIndex / 4) + "-" + card.id}
                className="zv-benefit-card"
                data-accent={card.accent}
                aria-hidden={!interactive}
              >
                <div className={"zv-benefit-art" + (card.type === "cash" ? " zv-benefit-art-cash" : "")}>
                  {card.type === "cash"
                    ? <img
                        src={asset("pebble-health-cash.webp")}
                        alt="Black gloved hands presenting a cloche on a serving tray"
                        width="237" height="356" loading="lazy"
                      />
                    : <img
                        src={asset("pebble-imgDesigner81.png")}
                        alt="Hands holding a warm drink"
                        loading="lazy"
                      />
                  }
                </div>
                <DesignIcon name="pebble-imgVector5.svg" className="zv-benefit-spark" />
                <h3>{card.title}</h3>
                <p>{card.description}</p>
                <ul className="zv-benefit-features">
                  {card.features.map((feature) => <li key={feature}>{feature}</li>)}
                </ul>
                <a
                  className="zv-button"
                  href={PEBBLE_URL}
                  target="_blank"
                  rel="noreferrer"
                  tabIndex={interactive ? 0 : -1}
                >
                  Explore pebble
                </a>
              </article>
            );
          })}
        </div>
      </div>
      <div className="zv-benefits-carousel-controls">
        <button type="button" className="zv-benefits-arrow" onClick={() => move(-1)} aria-label="Previous benefit">‹</button>
        <div className="zv-benefits-pagination" role="group" aria-label="Choose benefit">
          {Array.from({ length: pageCount }, (_, index) => (
            <button
              key={index}
              type="button"
              className={"zv-benefits-dot" + (index === activePage ? " is-active" : "")}
              aria-label={"Show page " + (index + 1) + " of " + pageCount}
              aria-current={index === activePage ? "true" : undefined}
              onClick={() => goTo(index)}
            />
          ))}
        </div>
        <button type="button" className="zv-benefits-arrow" onClick={() => move(1)} aria-label="Next benefit">›</button>
      </div>
    </div>
  );
}

const pebbleFaqs = [
  {
    question: "What is Pebble?",
    answer: "Pebble helps you explore hospital cash protection through a WhatsApp-first experience. The current options, eligibility rules, and benefit limits are explained before you join.",
  },
  {
    question: "How does my airtime or data purchase help?",
    answer: "Start a chat with Pebble and follow the airtime or data purchase flow. Ask for the contribution and coverage details attached to eligible purchases before paying.",
  },
  {
    question: "How do I make a claim?",
    answer: "Message Pebble on WhatsApp and follow the claims instructions. You may be asked for hospital admission evidence and other details so the claim can be reviewed against the applicable terms.",
  },
  {
    question: "When will my cover begin?",
    answer: "The start date, waiting period, exclusions, and payment conditions depend on the plan. Request the current policy details through Pebble before relying on any benefit.",
  },
];

function PebbleFAQ() {
  const [expanded, setExpanded] = useState(0);

  return (
    <section className="zv-pebble-faq" id="pebble-faq" aria-labelledby="zv-pebble-faq-title">
      <span className="zv-pebble-faq-spark zv-pebble-faq-spark-left" aria-hidden="true">✦</span>
      <div className="zv-container">
        <header className="zv-pebble-faq-heading">
          <h2 id="zv-pebble-faq-title">Frequently asked questions</h2>
          <p>Everything you need to know about Pebble.</p>
        </header>
        <div className="zv-pebble-faq-panel">
          {pebbleFaqs.map((item, index) => (
            <div className={"zv-pebble-faq-item" + (expanded === index ? " is-open" : "")} key={item.question}>
              <h3>
                <button
                  type="button"
                  id={`zv-pebble-faq-question-${index}`}
                  aria-expanded={expanded === index}
                  aria-controls={`zv-pebble-faq-answer-${index}`}
                  onClick={() => setExpanded(expanded === index ? -1 : index)}
                >
                  <span>{item.question}</span>
                  <span className="zv-pebble-faq-toggle" aria-hidden="true">{expanded === index ? "−" : "+"}</span>
                </button>
              </h3>
              <div
                id={`zv-pebble-faq-answer-${index}`}
                className="zv-pebble-faq-answer"
                role="region"
                aria-labelledby={`zv-pebble-faq-question-${index}`}
                aria-hidden={expanded !== index}
              >
                <div className="zv-pebble-faq-answer-inner"><p>{item.answer}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <span className="zv-pebble-faq-spark zv-pebble-faq-spark-right" aria-hidden="true">✦</span>
    </section>
  );
}

function PebbleAvatar({ name, size }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "block",
        width: size,
        height: size,
        minWidth: size,
        flex: `0 0 ${size}px`,
        alignSelf: "center",
        borderRadius: "50%",
        overflow: "hidden",
      }}
    >
      <img
        src={asset(name)}
        alt=""
        width={size}
        height={size}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
        }}
      />
    </span>
  );
}

export default function Pebble() {
  // The third visible step is focused. The fourth begins offscreen.
  const [slideIndex, setSlideIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [resetting, setResetting] = useState(false);
  const resetTimeout = useRef(null);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    if (paused || reducedMotion || resetting) return undefined;
    const interval = window.setInterval(() => {
      setSlideIndex((current) => current + 1);
    }, 4500);
    return () => window.clearInterval(interval);
  }, [paused, reducedMotion, resetting]);

  useEffect(() => () => {
    if (resetTimeout.current != null) window.clearTimeout(resetTimeout.current);
  }, []);

  const settleLoop = (event) => {
    if (event.target !== event.currentTarget || event.propertyName !== "transform") return;
    // Slide from the last original window to the identical cloned window,
    // then silently move the track back without reversing its direction.
    if (slideIndex >= 8) {
      setResetting(true);
      setSlideIndex((current) => current % steps.length);
      if (resetTimeout.current != null) window.clearTimeout(resetTimeout.current);
      resetTimeout.current = window.setTimeout(() => {
        setResetting(false);
        resetTimeout.current = null;
      }, 70);
    }
  };

  const focusVisibleStep = (renderIndex) => {
    // Clicking an earlier visible row scrolls it into the bottom focus slot.
    setSlideIndex((current) => current + Math.max(0, 2 - (renderIndex - current)));
  };

  return (
    <div className="zv-site zv-pebble">
      <ArticleSeo
        title="Pebble | Zaviramed"
        description="Discover Pebble: hospital cash cover starting from the airtime and data you buy. Learn more on WhatsApp."
        canonical="https://www.zaviramed.com.ng/pebble"
        image="https://www.zaviramed.com.ng/design/pebble-imgHomepageFrame.png"
        type="website"
      />
      <Header />
      <main id="main-content">
        <section className="zv-pebble-hero-shell">
          <div className="zv-container">
            <div className="zv-pebble-hero">
              <img
                className="zv-hero-photo"
                src={asset("pebble-imgHomepageFrame.png")}
                alt=""
                fetchPriority="high"
              />
              <div className="zv-pebble-hero-copy">
                <h1>
                  Get <em>paid</em> when you’re admitted.
                </h1>
                <p>
                  Pebble pays you cash for every hospital stay or emergency.
                  Your cover comes from the airtime and data you already buy .
                </p>
                <WhatsAppButton href={PEBBLE_URL}>
                  Start on WhatsApp
                </WhatsAppButton>
              </div>
            </div>
          </div>
        </section>
        <section className="zv-pebble-problem">
          <div className="zv-container">
            <div className="zv-pebble-problem-card">
              <div className="zv-pebble-problem-image">
                <img
                  src={asset("pebble-imgImageContainer.jpg")}
                  alt="A smiling person looking up beneath flowers and a warm sky"
                />
                <div>
                  <p>
                    A hospital stay means paying bills and not earning, and most
                    people have nothing to fall back on.
                  </p>
                  <strong>
                    <em>Pebble</em> gives some of it back.
                  </strong>
                </div>
                <a href={PEBBLE_URL} target="_blank" rel="noreferrer">
                  <DesignIcon name="pebble-imgArrow1.svg" />
                  Get Covered
                </a>
              </div>
            </div>
          </div>
        </section>
        <section className="zv-pebble-steps">
          <div className="zv-container zv-pebble-steps-inner">
            <div
              className="zv-pebble-step-list"
              role="group"
              aria-label="How Pebble works - four steps"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onFocusCapture={() => setPaused(true)}
              onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
              }}
            >
              <div className="zv-pebble-step-viewport">
                <div
                  className={`zv-pebble-step-track${resetting ? " is-resetting" : ""}`}
                  style={{ "--zv-slide-index": slideIndex }}
                  onTransitionEnd={settleLoop}
                >
                  {loopingSteps.map((step, renderIndex) => {
                    const visible = renderIndex >= slideIndex && renderIndex < slideIndex + 3;
                    const focused = renderIndex === slideIndex + 2;
                    return (
                      <button
                        key={`${Math.floor(renderIndex / steps.length)}-${step.title}`}
                        type="button"
                        className="zv-pebble-step"
                        aria-current={focused ? "step" : undefined}
                        aria-hidden={!visible}
                        tabIndex={visible ? 0 : -1}
                        onClick={() => focusVisibleStep(renderIndex)}
                      >
                        <span className="zv-pebble-step-title">{step.title}</span>
                        <span className="zv-pebble-step-description">{step.description}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div
              className="zv-pebble-chat"
              aria-label="Example WhatsApp conversation with Pebble"
            >
              <div className="zv-pebble-chat-inner">
                <div className="zv-pebble-chat-header">
                  <PebbleAvatar name="pebble-imgEllipse6.jpg" size={54} />
                  <div>
                    <strong>
                      <DesignIcon name="pebble-imgEllipse7.svg" />
                      Kelvin
                    </strong>
                    <small>Claims manager at Pebble</small>
                  </div>
                </div>
                <p className="zv-chat-today">Today</p>
                <div className="zv-pebble-chat-message">
                  <PebbleAvatar name="pebble-imgEllipse8.jpg" size={42} />
                  <div>
                    <strong>Welcome to Pebble</strong>
                    <p>Im Kelvin, your claims specialiat</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="zv-pebble-topup">
          <div className="zv-container">
            <img src={asset("pebble-imgImageContainer1.png")} alt="" />
            <div>
              <h2>Top up airtime or data to boost your cover</h2>
              <p>
                Recharge through Pebble on WhatsApp and explore how eligible
                <br className="zv-pebble-desktop-break" />
                top-ups can contribute toward your cover.
              </p>
              <WhatsAppButton href={PEBBLE_URL}>
                Start on WhatsApp
              </WhatsAppButton>
            </div>
          </div>
        </section>
        <section className="zv-access zv-pebble-claims">
          <div className="zv-container zv-access-inner">
            <div className="zv-access-copy">
              <div className="zv-access-intro">
                <h2>
                  Claims paid in 48
                  <br />
                  hours.
                </h2>
                <p>
                  No papers. No walk-ins. You claim on WhatsApp, wherever you
                  are.
                </p>
              </div>
              <WhatsAppButton href={PEBBLE_URL} dark>
                Start on WhatsApp
              </WhatsAppButton>
            </div>
            <div className="zv-offers-side">
              <div className="zv-claim-cards">
                {[
                  [
                    "Send “Claim” to Pebble",
                    "Do it from your phone, any time.",
                  ],
                  [
                    "Tell us what happened",
                    "Say whether it was an admission or an A&E visit, and send your proof.",
                  ],
                  [
                    "We review and pay",
                    "Once your claim is approved, the cash reaches you within 48 hours.",
                  ],
                ].map(([title, body]) => (
                  <div key={title}>
                    <h3>{title}</h3>
                    <p>{body}</p>
                  </div>
                ))}
              </div>
            </div>
            <DesignIcon
              name="pebble-imgVector4.svg"
              className="zv-access-spark"
            />
          </div>
        </section>
        <section className="zv-pebble-partners">
          <div className="zv-container">
            <img
              src={asset("pebble-imgFrame50.png")}
              alt="Pebble cover partner"
            />
            <h2>
              <em>Pebble’s</em> cover is provided by Casava, Gosi regulated by
              NAICOM
            </h2>
          </div>
        </section>
        <section className="zv-pebble-benefits">
          <div className="zv-container">
            <h2>Enjoy amazing benefits</h2>
            <p className="zv-section-subtitle">
              Get insured for less than the
              <br />
              cost to recharge.
            </p>
            <BenefitsCarousel />
          </div>
        </section>
        <PatientReviews pebble />
        <PebbleFAQ />
        <DoctorBanner />
      </main>
      <Footer />
    </div>
  );
}
